// apps/admin/src/api/systemSettings.api.ts
//
// Phase 8.3 Wave 6 Task 41 — System Settings API client (7-namespace CRUD).
//
// vg-binding: CONTEXT:decisions=P8.D-38,P8.D-40,P8.D-100,P8.D-101,P8.D-120,P8.D-170,P8.D-219,P8.D-466,P8.D-478
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Mirrors the contract published in
//   .vg/phases/08.3-admin-cms-system/API-CONTRACTS.md § Section 3 — System
//   Settings (GET/PATCH /api/v1/admin/settings/:namespace) and the live BE
//   plugin landed by task-12 at
//   apps/api/src/modules/system-settings/system-settings.routes.ts
//
// Endpoints:
//   - GET   /api/v1/admin/settings/:namespace
//       Permission: `system:settings:read`
//       Returns { namespace, values, version, updated_by, updated_by_name,
//                 updated_at }.
//   - PATCH /api/v1/admin/settings/:namespace
//       Permission: `system:settings:write:${namespace}` (per-namespace gate
//        parent-child rule).
//       Body: { patch: Record<string,unknown>, expected_version: number }.
//       Throws ApiError (status 409, code VERSION_CONFLICT) with
//       `details: { current_version, current_values, expected_version }`
//       on optimistic-lock mismatch.
//
// Response envelope per INTERFACE-STANDARDS §response-envelope:
//   { ok:true, data:{ namespace, values, version, ... }, message?, request_id? }
// Error envelope per INTERFACE-STANDARDS §error-shape (handled by apiClient):
//   { ok:false, error:{ code, message, user_message?, details?, request_id? } }
//
// FE message priority (per INTERFACE-STANDARDS §message-priority):
//   error.user_message → error.message → message → NETWORK_FALLBACK

import { apiClient, ApiError } from '../lib/apiClient.js';

// ---------------------------------------------------------------------------
// Namespace catalog (mirrors backend SETTINGS_NAMESPACES from
// apps/api/src/modules/system-settings/system-settings.model.ts).
// ---------------------------------------------------------------------------

export const SETTINGS_NAMESPACES = [
  'general',
  'merchant',
  'vendor',
  'order',
  'notifications',
  'security',
  'support',
] as const;

export type SettingsNamespace = (typeof SETTINGS_NAMESPACES)[number];

// ---------------------------------------------------------------------------
// Wire types — namespace envelope shape post-apiClient unwrap. apiClient
// unwraps the top-level `{ ok, data }` envelope so callers receive `data`.
// ---------------------------------------------------------------------------

export interface SettingsEnvelope<TValues = Record<string, unknown>> {
  namespace: SettingsNamespace;
  values: TValues;
  version: number;
  updated_by: string | null;
  updated_by_name: string | null;
  updated_at: string | null;
}

export interface PatchSettingsResult<TValues = Record<string, unknown>> {
  namespace: SettingsNamespace;
  values: TValues;
  version: number;
  diff: Array<{ path: string; before: unknown; after: unknown }>;
}

// ---------------------------------------------------------------------------
// Per-namespace strict-typed value shapes (mirror API-CONTRACTS § GET
// /api/v1/admin/settings/general | merchant | vendor strict schemas).
// Only the three namespaces consumed by this task are typed; the rest are
// loose `Record<string, unknown>` and live in sibling tasks 42/43.
// ---------------------------------------------------------------------------

export interface GeneralSettingsValues {
  platform_name: string;
  logo_url: string | null;
  favicon_url: string | null;
  default_timezone: string;
  default_date_format: string;
  default_locale: 'en' | 'vi' | 'fr';
  session_timeout_minutes: number;
  maintenance: {
    active: boolean;
    custom_message: string;
    duration_hours: number;
    bypass_ips: string[];
  };
}

export interface MerchantSettingsValues {
  default_credit_rules: Record<string, unknown>;
  auto_cancel_draft_minutes: number;
  registration_survey_id: string | null;
  tier_evaluation_window_days: '30' | '60' | '90' | 'lifetime';
  tier_rules: Array<{ tier: string; criteria: Record<string, unknown> }>;
  //  At-Risk thresholds (also surfaced in merchant tab per task brief).
  at_risk_thresholds?: {
    low_rating_below: number;
    high_rejection_above: number;
    complaint_spike_count: number;
    complaint_window_hours: number;
  };
  //  invoice numbering pattern lives under `order` namespace per the
  // strict contract, but the task brief surfaces it on the Merchant tab as a
  // convenience read-only mirror — handled in MerchantConfigsTab via the
  // `order` namespace fetch.
}

export interface VendorSettingsValues {
  default_sla_thresholds: Record<string, number>;
  payout_schedule: string;
  hold_period_by_tier: Record<string, number>;
  capacity_alerts: { low: number; warning: number; critical: number };
  at_risk_thresholds: {
    low_rating_below: number;
    high_rejection_above: number;
    complaint_spike_count: number;
    complaint_window_hours: number;
  };
  low_stock_escalation_sla_hours: number;
}

// ---------------------------------------------------------------------------
// API verbs
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/admin/settings/:namespace
 *
 * Requires permission `system:settings:read`. Returns the namespace envelope
 * with the current `version` integer — caller must echo this as
 * `expected_version` on PATCH so the BE can enforce optimistic lock.
 */
export async function getSettings<TValues = Record<string, unknown>>(
  namespace: SettingsNamespace,
): Promise<SettingsEnvelope<TValues>> {
  return apiClient.get<SettingsEnvelope<TValues>>(
    `/admin/settings/${namespace}`,
  );
}

/**
 * PATCH /api/v1/admin/settings/:namespace
 *
 * Requires permission `system:settings:write:${namespace}`. The BE merges the
 * partial `patch` into the stored values document, bumps `version` atomically,
 * and emits one audit_log row per changed leaf path.
 *
 * Throws `ApiError` with `code='VERSION_CONFLICT'` (status 409) when the
 * caller's `expected_version` differs from the persisted version — the
 * envelope's `error.details` carries `current_version` + `current_values`
 * + `expected_version` so the FE conflict-resolution UX can diff the two
 * sides without an extra GET round-trip.
 */
export async function patchSettings<TValues = Record<string, unknown>>(
  namespace: SettingsNamespace,
  patch: Record<string, unknown>,
  expectedVersion: number,
): Promise<PatchSettingsResult<TValues>> {
  return apiClient.patch<PatchSettingsResult<TValues>>(
    `/admin/settings/${namespace}`,
    { patch, expected_version: expectedVersion },
  );
}

// ---------------------------------------------------------------------------
// Error helpers
// ---------------------------------------------------------------------------

/** Narrow an `ApiError` to the VERSION_CONFLICT 409 case + extract details. */
export function isVersionConflict(
  err: unknown,
): err is ApiError & {
  details: {
    current_version: number;
    current_values: Record<string, unknown>;
    expected_version: number;
  };
} {
  if (!(err instanceof ApiError)) return false;
  if (err.code !== 'VERSION_CONFLICT') return false;
  const d = err.details;
  return (
    !!d &&
    typeof d === 'object' &&
    typeof (d as { current_version?: unknown }).current_version === 'number'
  );
}
